import { Global } from '@emotion/react';
import { styleSkeleton } from '@inflearn/app-shell/dist/skeleton';
import { config as faConfig } from '@inflearn/fontawesome-svg-core';
import type { NextPage } from 'next';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';

import { NEXT_PUBLIC_COMMIT_HASH, NEXT_PUBLIC_USE_MSW, NEXT_PUBLIC_VERSION } from '../../appConfig';
import { initMocks } from '../../mocks';
import { AnalyticsProvider } from '../providers/AnalyticsProvider';
import { LoginModalProvider } from '../providers/ControlLoginModalContextProvider';
import { DsProvider } from '../providers/DsProvider';
import { HackleProvider } from '../providers/HackleProvider';
import { QueryClientProvider } from '../providers/QueryClientProvider';

import '@inflearn/ds-image-cropper/dist/index.css';
import '@inflearn/ds-markdown-body/dist/index.css';
import '@inflearn/editor/dist/style.css';
import '@inflearn/fontawesome-svg-core/styles.css';
import '@inflearn/ds-table/dist/ag-grid.css';
import '@inflearn/ds-table/dist/fontawesome-pro-solid.css';
import '../styles/globals.css';
import '../styles/markdown-body.css';

import dynamic from 'next/dynamic';
import { initMonitoring } from '../components/Monitoring/initMonitoring';
import SetUserToThirdParty from '../components/common/SetUserToThirdParty';
import { useInAppBrowserBypass } from '../hooks/useInAppBrowserBypass';
import { useSetLocales } from '../hooks/useSetLocales';

const Monitoring = dynamic(
  () => import('../components/Monitoring/Monitoring').then((mod) => mod.Monitoring),
  {
    ssr: false,
  },
);

faConfig.autoAddCss = false;

if (NEXT_PUBLIC_USE_MSW === 'true') {
  await initMocks();
}

if (typeof window !== 'undefined' && NEXT_PUBLIC_COMMIT_HASH) {
  window.__SHA__ = NEXT_PUBLIC_COMMIT_HASH;
}

if (typeof window !== 'undefined' && NEXT_PUBLIC_VERSION) {
  window.__RELEASE__ = NEXT_PUBLIC_VERSION;
}

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pathname: string) => ReactElement;
};

type AppPropsWithLayout = AppProps<{ dehydratedState?: unknown }> & {
  Component: NextPageWithLayout;
};

if (typeof window !== 'undefined') {
  console.time('im');
  await initMonitoring();
  console.timeEnd('im');
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const pathname = router.pathname;
  const getLayout = Component.getLayout || ((page) => page);
  const { dehydratedState, ...restPageProps } = pageProps;

  useSetLocales();
  useInAppBrowserBypass();

  return (
    <>
      <QueryClientProvider dehydratedState={dehydratedState}>
        <DsProvider>
          <HackleProvider>
            <AnalyticsProvider>
              <LoginModalProvider>
                <SetUserToThirdParty />
                <Monitoring />
                <Global styles={[styleSkeleton]} />
                {getLayout(<Component {...restPageProps} />, pathname)}
              </LoginModalProvider>
            </AnalyticsProvider>
          </HackleProvider>
        </DsProvider>
      </QueryClientProvider>
    </>
  );
}

export default appWithTranslation(App);
